import { inject as service } from '@ember/service';
import Controller, { inject as controller } from '@ember/controller';
import { action } from '@ember/object';
import { task, timeout } from 'ember-concurrency';
import RouterService from '@ember/routing/router-service';
import ApiService, { RequestOptions } from 'cio/services/api';
import AccountService from 'cio/services/account';
import AnalyticsService from 'cio/services/analytics';
import { tracked } from '@glimmer/tracking';
import AlertsService from 'cio/services/alerts';
import { ProductApp, ProductAppInfo, isProductReady, productActivationInfo } from 'cio/utils/product-app-info';
import EnvironmentService from 'cio/services/environment';
import HydraService, { HydraWorkspace } from 'cio/services/hydra';
import config from 'cio/config/environment';
import RavenService from 'cio/services/raven';
import ApiErrorsService from 'cio/services/api-errors';
import { announceRefreshWorkspaces } from '@customerio/hydra-nav-state';
import { DataCenter } from 'cio/models/account';

type FormValues = {
  useCase: string;
  useCaseDetail: string;
  platform: string;
  platformOther: string;
  employeeCount: string;
};

const ROUTE_TRANSITION_MAP: Record<string, string> = {
  '': 'activate.:product',
  'activate.:product': 'product-activation.experience',
  'product-activation.experience': 'product-activation.goals',
  'product-activation.goals': '',
};

const PRODUCT_READY_TRIES_ALLOWED = 20;

export default class ProductActivationController extends Controller {
  @service router!: RouterService;
  @service api!: ApiService;
  @service account!: AccountService;
  @service analytics!: AnalyticsService;
  @service alerts!: AlertsService;
  @service environment!: EnvironmentService;
  @service raven!: RavenService;
  @service apiErrors!: ApiErrorsService;
  @service hydra!: HydraService;

  @controller('authenticated') authenticated!: Controller;

  @tracked formValues = this.product?.formValues;
  @tracked currentRouteName = '';

  declare model: ProductApp;

  get product() {
    return productActivationInfo[this.model];
  }

  goToNextStep() {
    let { currentRouteName } = this.router as { currentRouteName: string };
    this.currentRouteName = currentRouteName;
    let nextRouteName = ROUTE_TRANSITION_MAP[currentRouteName];
    if (nextRouteName) {
      this.router.transitionTo(nextRouteName);
    }
  }

  activateProductTask = task(async (product: ProductAppInfo) => {
    try {
      let options: RequestOptions = {
        pathScope: 'account',
        method: 'POST',
        data: { product: product.name },
        dataCenter: DataCenter.US, // this endpoint changes CIO's data on global DB. Better to always use US DC.
      };
      await this.api.request('activate_product', options);
      await this.pollForProductReadyTask.perform(product);
      await announceRefreshWorkspaces();
      this.activationCompleted();
    } catch (e) {
      this.alerts.error(
        'activation.error',
        'Something went wrong. Please try again or contact win@customer.io for support.',
      );
    }
  });

  // I think this should be moved to the account service but was going to wait for
  // this PR https://github.com/customerio/ui/pull/7293/files to be merged before
  // doing that so that we can see what can be reused.
  pollForProductReadyTask = task(async (product: ProductAppInfo) => {
    let productReady = false;
    while (!productReady) {
      let tries = 0;
      if (tries <= PRODUCT_READY_TRIES_ALLOWED) {
        tries++;
        let workspacesResponse = await this.api.request<HydraWorkspace[]>('workspaces', {
          pathScope: 'hydra_account',
        });

        if (workspacesResponse.length > 0) {
          productReady = isProductReady(product.name as ProductApp, workspacesResponse[0]);
        }

        if (productReady) {
          break;
        }
        // use same timeout delay as signup does
        await timeout(config.APP.signupPollingDelay);
      } else {
        let errorMessage = `Error activating product: ${product.name}. Timeout reached.`;
        // if we've tried 20 times and it's still not ready, throw an error
        // and log the error in sentry
        this.raven.captureException(new Error(errorMessage));

        // Notify the user that something went wrong
        this.alerts.error(
          'activation.error',
          'Something went wrong. Please try again or contact win@customer.io for support.',
        );

        // Throw this error so that the task enters a failed state
        throw new Error(errorMessage);
      }
    }
  });

  setDefaults() {
    this.set('formValues', this.product?.formValues);
  }

  @action
  activationStarted() {
    let { id, currentUser } = this.account.current;
    let data = {
      account_id: id,
      product: this.product.name,
      email: currentUser.email,
    };
    this.analytics.track('product_activation', 'activation_started', { data });
  }

  @action
  activationStepCompleted(step: string) {
    let { id, currentUser } = this.account.current;
    let data = {
      account_id: id,
      product: this.product.name,
      step,
      email: currentUser.email,
    };
    this.analytics.track('product_activation', 'activation_step_completed', { data });
  }

  @action
  activationCompleted() {
    let { id } = this.account.current;
    let data = {
      account_id: id,
      product: this.product.name,
      use_case: this.formValues.useCase,
      use_case_detail: this.formValues.useCaseDetail,
      platform: this.formValues.platform,
      platform_other: this.formValues.platformOther,
      employee_count: this.formValues.employeeCount,
    };
    this.analytics.track('product_activation', 'activation_completed', { data });
  }

  @action
  setFormValue<T extends keyof FormValues>(key: T, value: FormValues[T]) {
    this.formValues = { ...this.formValues, [key]: value };
  }

  @action
  getFormValues<T extends keyof FormValues>(key: T) {
    return this.formValues[key];
  }
}

declare module '@ember/controller' {
  interface Registry {
    'product-activation': ProductActivationController;
  }
}
